import Comscore from './trackers/Comscore';
import Dsp from './trackers/Dsp';
import Gtm from './trackers/Gtm';
import Native from './trackers/Native';
import Ua from './trackers/Ua';
import type Tracker from './trackers/Tracker';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { optanonUtils } from './utils/optanonUtils';

function stripId(type: string, id: string): string {
  if (type === 'comscore') {
    return id.replace(/\D/g, '');
  } else {
    return id;
  }
}

interface AnalyticsInterface {
  anonymousId?: string;
  drymode: boolean;
  id: string;
  encodedGuid?: string;
  encodedZuid?: string;
}

export default class Analytics {
  public anonymousId?: string;
  public drymode: boolean;
  public id?: string;
  public logger = getGlobalLogger();
  public tracker: Tracker;
  public type: string;
  public encodedGuid?: string;
  public encodedZuid?: string;

  constructor(options: AnalyticsInterface) {
    const { anonymousId, drymode, id, encodedGuid, encodedZuid } = options;
    this.anonymousId = anonymousId;
    this.drymode = drymode;
    this.type = this.getAnalyticsType(id);
    this.id = stripId(this.type, id);
    this.encodedGuid = encodedGuid;
    this.encodedZuid = encodedZuid;
    this.tracker = this.makeAnalyticsTracker();

    this.registerOnConsentChanged();
    this.stopDisabledTracker();
  }

  getAnalyticsType(id: string): string {
    const parsedType = id.split('-')[0];

    if (parsedType.toLowerCase().indexOf('ua') > -1) {
      return 'ua';
    } else if (parsedType.toLowerCase().indexOf('gtm') > -1) {
      return 'gtm';
    } else if (parsedType.toLowerCase().indexOf('com') > -1) {
      return 'comscore';
    } else if (parsedType.toLowerCase().indexOf('dsp') > -1) {
      return 'dsp';
    } else if (parsedType.toLowerCase().indexOf('native') > -1) {
      return 'native';
    } else {
      this.logger &&
        this.logger.warn(
          {
            id,
          },
          'could not determine analytics type for id',
        );

      return '';
    }
  }

  makeAnalyticsTracker(): Tracker {
    const { anonymousId = '', drymode, id = '', encodedGuid, encodedZuid } = this;
    if (this.type === 'gtm') {
      return new Gtm({
        drymode,
        id,
      });
    } else if (this.type === 'ua') {
      return new Ua({
        drymode,
        id,
      });
    } else if (this.type === 'dsp') {
      return new Dsp({
        id,
        anonymousId,
        drymode,
        apiKey: id,
      });
    } else if (this.type === 'comscore') {
      return new Comscore({
        drymode,
        id,
        encodedGuid,
        encodedZuid,
      });
    }
    return new Native({
      drymode,
      id,
    });
  }

  registerOnConsentChanged() {
    optanonUtils().asyncHandleOnConsentChanged(() => {
      this.stopDisabledTracker();
    });
  }

  stopDisabledTracker() {
    if (typeof window !== 'undefined') {
      this.tracker.stopDisabledTracker();
    }
  }
}
